import React, { useState, useEffect } from 'react';
import { EnrichedVisitor } from "@/types/database";
import VisitorsHeader from "./components/VisitorsHeader";
import VisitorsTable from "./components/VisitorsTable";
import VisitorDialog from "./VisitorDialog";
import { useVisitorSelection } from "@/hooks/useVisitorSelection";
import { useVisitorsFiltering } from './hooks/useVisitorsFiltering';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Download, ArrowUp, ArrowDown } from 'lucide-react';
import Pagination from './components/Pagination';
import { BlockCompanyDialog } from './BlockCompanyDialog';
import { useBlockedCompanies } from '@/hooks/useBlockedCompanies';
import { useAuth } from '@/components/AuthProvider';
import { supabase } from "@/integrations/supabase/client";
import VisitorsFilterModal from './VisitorsFilterModal';
import { VisitorsFilters } from './VisitorsFilterBar';

interface VisitorsListViewProps {
  visitors: EnrichedVisitor[];
  currentPage: number;
  onPageChange: (page: number) => void;
  itemsPerPage: number;
  isLoading?: boolean;
}

type SortField = 'company_name' | 'company_industry' | 'location_country' | 'created_at' | 'company_size';
type SortDirection = 'asc' | 'desc';

const VisitorsListView = ({ 
  visitors, 
  currentPage,
  onPageChange,
  itemsPerPage,
  isLoading = false
}: VisitorsListViewProps) => {
  const [selectedVisitor, setSelectedVisitor] = useState<EnrichedVisitor | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [sortField, setSortField] = useState<SortField>('created_at');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [blockDialogOpen, setBlockDialogOpen] = useState(false);
  const [selectedVisitorToBlock, setSelectedVisitorToBlock] = useState<EnrichedVisitor | null>(null);
  const [userOrganization, setUserOrganization] = useState<string | undefined>(undefined);
  
  const [filterModalOpen, setFilterModalOpen] = useState(false);

  const {
    searchTerm,
    setSearchTerm,
    filters,
    handleFilterChange,
    filteredVisitors,
    industries,
    companySizes,
    locations,
    personRoles,
    seniorityLevels,
    revenueMin,
    revenueMax
  } = useVisitorsFiltering(visitors);

  const applyFilters = (next: VisitorsFilters) => {
    handleFilterChange("industry", next.industry);
    handleFilterChange("companySize", next.companySize);
    handleFilterChange("location", next.location);
    handleFilterChange("personRole", next.personRole);
    handleFilterChange("seniorityLevel", next.seniorityLevel);
    handleFilterChange("revenueMin", next.revenueMin ?? revenueMin);
    handleFilterChange("revenueMax", next.revenueMax ?? revenueMax);
  };

  const resetFilters = () => {
    handleFilterChange("industry", "all");
    handleFilterChange("companySize", "all");
    handleFilterChange("location", "all");
    handleFilterChange("personRole", "all");
    handleFilterChange("seniorityLevel", "all");
    handleFilterChange("revenueMin", revenueMin);
    handleFilterChange("revenueMax", revenueMax);
  };

  const handleVisitorClick = (visitor: EnrichedVisitor) => {
    setSelectedVisitor(visitor);
    setDialogOpen(true);
  };

  const toggleSortDirection = () => {
    setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
  };

  const sortedVisitors = [...filteredVisitors].sort((a, b) => {
    const compare = (valA: any, valB: any) => {
      if (valA === null || valA === undefined) return sortDirection === 'asc' ? -1 : 1;
      if (valB === null || valB === undefined) return sortDirection === 'asc' ? 1 : -1;
      
      if (typeof valA === 'string' && typeof valB === 'string') {
        return sortDirection === 'asc' 
          ? valA.localeCompare(valB) 
          : valB.localeCompare(valA);
      }
      
      return sortDirection === 'asc' ? valA - valB : valB - valA;
    };

    switch (sortField) {
      case 'company_name':
        return compare(a.company_name?.toLowerCase(), b.company_name?.toLowerCase());
      case 'company_industry':
        return compare(a.company_industry?.toLowerCase(), b.company_industry?.toLowerCase());
      case 'location_country':
        return compare(a.location_country?.toLowerCase(), b.location_country?.toLowerCase());
      case 'company_size':
        const sizeOrder = { 'Small': 1, 'Medium': 2, 'Large': 3 };
        const sizeA = a.company_size ? sizeOrder[a.company_size as keyof typeof sizeOrder] || 0 : 0;
        const sizeB = b.company_size ? sizeOrder[b.company_size as keyof typeof sizeOrder] || 0 : 0;
        return sortDirection === 'asc' ? sizeA - sizeB : sizeB - sizeA;
      case 'created_at':
      default:
        return compare(new Date(a.created_at).getTime(), new Date(b.created_at).getTime());
    }
  });

  const totalItems = sortedVisitors.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalItems);
  const visibleVisitors = sortedVisitors.slice(startIndex, endIndex);
  
  const { selectedVisitors, toggleVisitor, togglePage, clearSelection, exportToCSV } = 
    useVisitorSelection(filteredVisitors);
    
  const allSelected = visibleVisitors.length > 0 && 
    visibleVisitors.every(visitor => selectedVisitors.has(visitor.id));

  const { user } = useAuth();

  const SkeletonLoader = () => (
    <div className="space-y-4 py-4">
      {[...Array(itemsPerPage)].map((_, i) => (
        <div key={i} className="min-h-[84px] w-full bg-gray-100 rounded-lg flex items-center px-6 animate-pulse gap-3">
          <div className="w-16 h-16 bg-gray-200 rounded-md mr-4" />
          <div className="flex-1 space-y-2">
            <div className="w-1/3 h-4 bg-gray-200 rounded" />
            <div className="w-1/4 h-4 bg-gray-200 rounded" />
            <div className="w-2/3 h-3 bg-gray-200 rounded mt-2" />
          </div>
        </div>
      ))}
    </div>
  );

  useEffect(() => {
    const fetchUserOrganization = async () => {
      if (!user?.id) return;
      
      try {
        const { data, error } = await supabase
          .from('organizations')
          .select('id')
          .eq('created_by', user.id)
          .single();
          
        if (error) {
          console.error("Error fetching user organization:", error);
          
          const { data: teamData, error: teamError } = await supabase
            .from('team_members')
            .select('organization_id')
            .eq('user_id', user.id)
            .eq('status', 'active')
            .not('organization_id', 'is', null)
            .single();
            
          if (teamError) {
            console.error("Error fetching team organization:", teamError);
            return;
          }
          
          if (teamData?.organization_id) {
            console.log("Found organization from team member record:", teamData.organization_id);
            setUserOrganization(teamData.organization_id);
          }
          return;
        }
        
        if (data?.id) {
          console.log("Found organization created by user:", data.id);
          setUserOrganization(data.id);
        }
      } catch (err) {
        console.error("Error in organization fetch:", err);
      }
    };
    
    fetchUserOrganization();
  }, [user?.id]);

  const { blockCompany } = useBlockedCompanies(userOrganization);

  const handleBlockVisitor = (visitor: EnrichedVisitor) => {
    setSelectedVisitorToBlock(visitor);
    setBlockDialogOpen(true);
  };

  const handleConfirmBlock = async (data: { companyName: string; domain: string; notes?: string; organizationId?: string }) => {
    console.log("Confirming block with data:", data);
    
    try {
      await blockCompany.mutateAsync({
        companyName: data.companyName,
        domain: data.domain,
        notes: data.notes,
        organizationId: userOrganization
      });
    } catch (error) {
      console.error("Failed to block company:", error);
    }
  };

  const handleExportSelected = () => {
    exportToCSV(filteredVisitors);
  };

  return (
    <div className="space-y-4 w-full overflow-hidden">
      <VisitorsHeader 
        totalVisitors={totalItems} 
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
      />

      <div className="mb-4 flex justify-between items-center">
        <Button 
          variant="outline"
          size="sm"
          className="flex items-center gap-2"
          onClick={() => setFilterModalOpen(true)}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-[#9b87f5]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path d="M4 21v2M4 21h16v2M20 21V7.5M20 7.5c0-1.326-1.074-2.4-2.4-2.4H6.4A2.4 2.4 0 0 0 4 7.5m16 0V3a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v4.5" stroke="#9b87f5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
          Filters
        </Button>

        {selectedVisitors.size > 0 && (
          <Button 
            variant="outline"
            size="sm"
            className="flex items-center gap-2"
            onClick={handleExportSelected}
          >
            <Download className="h-4 w-4" />
            Export {selectedVisitors.size} Selected Lead{selectedVisitors.size > 1 ? 's' : ''}
          </Button>
        )}
        
        <VisitorsFilterModal
          open={filterModalOpen}
          onOpenChange={setFilterModalOpen}
          onApply={applyFilters}
          onReset={resetFilters}
          filters={filters}
          industries={industries}
          companySizes={companySizes}
          locations={locations}
          personRoles={personRoles}
          seniorityLevels={seniorityLevels}
          revenueMin={revenueMin}
          revenueMax={revenueMax}
        />
      </div>

      {isLoading ? (
        <SkeletonLoader />
      ) : (
        <>
          <div className="w-full overflow-hidden">
            <div className="bg-white rounded-lg shadow border p-0 overflow-hidden">
              <VisitorsTable 
                visitors={visibleVisitors} 
                onViewVisitor={handleVisitorClick}
                onBlockVisitor={handleBlockVisitor}
                selectedVisitors={selectedVisitors}
                onToggleVisitor={toggleVisitor}
                onToggleAll={() => togglePage(visibleVisitors)}
                allSelected={allSelected}
                isLoading={isLoading}
              />
            </div>
            {totalPages > 1 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={onPageChange}
              />
            )}
          </div>
        </>
      )}

      <VisitorDialog 
        visitor={selectedVisitor} 
        isOpen={dialogOpen}
        onOpenChange={setDialogOpen}
      />

      <BlockCompanyDialog
        visitor={selectedVisitorToBlock}
        isOpen={blockDialogOpen}
        onOpenChange={setBlockDialogOpen}
        onConfirm={handleConfirmBlock}
        organizationId={userOrganization}
      />
    </div>
  );
};

export default VisitorsListView;
